import React, { useMemo } from 'react';
import { ProfilePicture } from '@getro/rombo';
import moment from 'moment';
import { Text, Flex, Box } from 'rebass/styled-components';
import { displayCompensationPeriod } from 'helpers/compensation';
import { mapSeniorityType } from 'services/contacts/seniorityTypes';
import { SkeletonRow } from 'components/atoms/rowSkeleton';
import { getAuthor, getExpirationDate } from '../jobHelpers';
import strings from '../../../../strings';
import JobStatus from '../../../atoms/JobStatus';
import JobVisibility from '../../../atoms/JobVisibility';
import JobFeatured from '../jobFeatured';
import { PeopleFinderCell } from '../components/PeopleFinderCell';
import { ApplicationBoostCell } from '../components/ApplicationBoostCell';
import { ApplicantsCell } from '../components/ApplicantsCell';

const ROW_HEIGHT = 60;
const VIEWPORT_OFFSET = 10;

const getSkeletonRowCount = () => {
  if (typeof window === 'undefined') return 10;
  return Math.max(Math.floor((window.innerHeight - VIEWPORT_OFFSET) / ROW_HEIGHT), 5);
};

const createSkeletonRow = (index) => ({
  id: `skeleton-${index}`,
  rowId: `skeleton-${index}`,
  isSkeleton: true,
  selected: false,
  original: { id: `skeleton-${index}`, jobFunctions: [] },
  title: <SkeletonRow height={16} width={180} />,
  company: (
    <Flex alignItems="center" sx={{ gap: '8px' }}>
      <SkeletonRow height={24} width={24} />
      <SkeletonRow height={16} width={120} />
    </Flex>
  ),
  applicationBoostAgent: <SkeletonRow height={16} width={100} />,
  jobFunction: <SkeletonRow height={16} width={100} />,
  seniority: <SkeletonRow height={16} width={80} />,
  source: <SkeletonRow height={16} width={60} />,
  status: <SkeletonRow height={16} width={60} />,
  visibility: <SkeletonRow height={16} width={70} />,
  featured: <SkeletonRow height={16} width={50} />,
  peopleFinder: <SkeletonRow height={16} width={100} />,
  applicants: <SkeletonRow height={16} width={40} />,
  createdBy: <SkeletonRow height={16} width={100} />,
  createdAt: <SkeletonRow height={16} width={80} />,
  expirationDate: <SkeletonRow height={16} width={80} />,
  location: <SkeletonRow height={16} width={120} />,
  compensation: <SkeletonRow height={16} width={100} />,
});

export const useTableRows = ({
  jobs,
  onShowJobRulesClick,
  onFindCandidates,
  onOpenProject,
  onApplicantsClicked,
  showApplicants,
  hasConnect,
  onLaunchApplicationBoostAgent,
  showSkeleton = false,
}) =>
  useMemo(() => {
    if (showSkeleton) {
      const skeletonRowCount = getSkeletonRowCount();
      return Array.from({ length: skeletonRowCount }, (_, index) => createSkeletonRow(index));
    }

    return jobs.map((job) => {
      const { name: companyName } = job.organization;
      const { id: authorId } = job.author || {};
      const { selected } = job;

      return {
        original: job,
        id: job.id,
        slug: job.slug,
        authorId,
        selected,
        rowId: job.id,
        getRowHeight: () => 48,
        title: (
          <Flex width="100%" alignItems="flex-start" justifyContent="flex-start">
            <Flex flexDirection="column">
              <Text
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                color="text.main"
                fontWeight="500"
                fontSize={[1]}
              >
                {job.title}
              </Text>
            </Flex>
          </Flex>
        ),
        company: (
          <Flex alignItems="center" sx={{ gap: '8px' }} color="text.main">
            <Box width="24px" height="24px" sx={{ flexShrink: 0 }}>
              <ProfilePicture variant="square" imageUrl={job.organization.logoUrl} title={companyName} />
            </Box>
            <Text color="text.main" fontSize="14px">
              {companyName}
            </Text>
          </Flex>
        ),
        applicationBoostAgent: (
          <ApplicationBoostCell job={job} onLaunchApplicationBoostAgent={onLaunchApplicationBoostAgent} />
        ),
        jobFunction: (
          <Text
            sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            color="text.main"
          >
            {job.jobFunctions && job.jobFunctions.length ? job.jobFunctions.map((item) => item.name).join(', ') : '-'}
          </Text>
        ),
        seniority: (
          <Text fontSize={[1]} color="text.main">
            {job.seniority ? mapSeniorityType(job.seniority) : '-'}
          </Text>
        ),
        source: (
          <Text fontSize={[1]} color="text.main">
            {strings.jobs.job_sources_for_admin_portal[job.source]}
          </Text>
        ),
        status: (
          <Flex fontSize={[1]} alignItems="flex-start">
            <JobStatus job={job} />
          </Flex>
        ),
        visibility: (
          <Flex fontSize={[1]}>
            <JobVisibility visibility={job.visibility} onShowJobRulesClick={onShowJobRulesClick} />
          </Flex>
        ),
        featured: (
          <Flex fontSize={[1]}>
            <JobFeatured isFeatured={job.featured === 'featured'} />
          </Flex>
        ),
        peopleFinder: <PeopleFinderCell job={job} onFindCandidates={onFindCandidates} onOpenProject={onOpenProject} />,
        applicants: (
          <ApplicantsCell
            job={job}
            showApplicants={showApplicants}
            hasConnect={hasConnect}
            onApplicantsClicked={onApplicantsClicked}
          />
        ),
        createdBy: (
          <Flex width={[1]} flexDirection="column">
            <Text fontSize={[1]} color="text.main">
              {getAuthor(job)}
            </Text>
          </Flex>
        ),
        createdAt: (
          <Flex width={[1]} flexDirection="column">
            <Text fontSize={[1]} color="text.main">
              {moment(job.createdAt).format('D MMM YYYY')}
            </Text>
          </Flex>
        ),
        expirationDate: (
          <Flex width={[1]}>
            <Text fontSize={[1]} color="text.main">
              {getExpirationDate(job)}
            </Text>
          </Flex>
        ),
        location: (
          <Text fontSize={[1]} color="text.main">
            {job.locations && job.locations.length ? job.locations?.join(', ') : '-'}
          </Text>
        ),
        compensation: (
          <Text fontSize={[1]} color="text.main">
            {displayCompensationPeriod({
              currency: job.compensationCurrency,
              amountMinCents: job.compensationAmountMinCents,
              amountMaxCents: job.compensationAmountMaxCents,
              offersEquity: job.compensationOffersEquity,
              period: job.compensationPeriod,
            }) || '-'}
          </Text>
        ),
      };
    });
  }, [
    jobs,
    onLaunchApplicationBoostAgent,
    onShowJobRulesClick,
    onFindCandidates,
    onOpenProject,
    showApplicants,
    hasConnect,
    onApplicantsClicked,
    showSkeleton,
  ]);
